import type { GbpInsightsData } from '@/lib/types';
import { StaleStamp } from './part-status';

// design brief §5.4 — OPERATOR DECISION: four tiles, no sparkline/chart
// (impeccable bans sparkline decoration outright). 4-up desktop, 2×2 mobile.
// Labels are plain product English, not the raw Google metric names.
function tilesFrom(data: GbpInsightsData): { label: string; value: number }[] {
  return [
    { label: 'Seen in search', value: data.searchImpressions },
    { label: 'Direction requests', value: data.directionRequests },
    { label: 'Calls', value: data.callClicks },
    { label: 'Website clicks', value: data.websiteClicks },
  ];
}

export function GbpPerformanceTiles({
  data,
  fetchedAt,
}: {
  data: GbpInsightsData;
  fetchedAt: number | undefined;
}) {
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-3">
        <h2 className="text-base font-semibold tracking-tight">Performance</h2>
        <StaleStamp fetchedAt={fetchedAt} />
      </div>
      <p className="mb-4 text-xs text-text-tertiary">Last {data.rangeDays} days, from Google.</p>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {tilesFrom(data).map((t) => (
          <div key={t.label} className="rounded-lg border border-border-subtle bg-surface-page p-4">
            <p className="text-2xl font-bold tracking-tight text-foreground">
              {t.value.toLocaleString()}
            </p>
            <p className="mt-1 text-xs text-text-secondary">{t.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// Two distinct honest reasons the strip is empty — never collapsed into one
// message, because they say different things and the reader deserves the
// true one:
//   'no_metrics' — we DID hear back from Google, and Google's response had
//     no metric series at all (a brand-new location, GbpInsightsData.
//     hasMetrics === false). Speaks from Google's side because we genuinely
//     know Google has nothing yet.
//   'unavailable' — we have no insights data at all because the sync itself
//     never completed (GbpPart's data is undefined). Speaks from OUR side,
//     matching the location/reviews sections' own fallback voice ("No
//     listing details yet.", "Reviews unavailable right now.") — we do NOT
//     know whether Google has data, only that we couldn't fetch it.
export function GbpPerformanceEmpty({ reason }: { reason: 'no_metrics' | 'unavailable' }) {
  return (
    <div>
      <h2 className="mb-2 text-base font-semibold tracking-tight">Performance</h2>
      <p className="text-sm text-text-tertiary">
        {reason === 'no_metrics'
          ? "Google isn't showing performance data for this business yet."
          : "Couldn't load your performance numbers right now."}
      </p>
    </div>
  );
}
